// components/SiteImage.jsx
//
// Renders a site image (admin-managed via /api/site-images) with a
// brand-aligned skeleton loader and several performance optimizations:
//
//   1. Cloudinary URL transforms (f_auto / q_auto / dpr_auto / w_<n> / c_limit)
//      → serve WebP/AVIF at the right size, often 50-80% smaller bytes.
//   2. fetchpriority="high" on hero usage so the browser kicks off the
//      image download as early as possible.
//   3. A soft cream-and-gold shimmer placeholder while the image loads,
//      so the wait feels intentional and matches the home aesthetic
//      instead of showing a generic spinner.
//
// API is backwards-compatible: existing callers keep working unchanged.
// New optional props:  priority (bool),  width (number, target px).

import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSiteImages,
  removeFromSiteImagesCache,
} from "../actions/siteImageActions";

/**
 * If `url` is a Cloudinary delivery URL, inject responsive/quality transforms.
 * Any non-Cloudinary URL is returned untouched. Safe on undefined/empty input.
 */
const optimizeCloudinary = (url, targetW = 1400) => {
  if (!url || typeof url !== "string") return url;
  if (!/res\.cloudinary\.com\//.test(url)) return url;

  // If we (or the admin) already injected our transforms, don't double-inject.
  if (/\/(upload|fetch)\/[^/]*f_auto/.test(url)) return url;

  return url.replace(
    /\/(upload|fetch)\//,
    (_, kind) => `/${kind}/f_auto,q_auto,dpr_auto,w_${targetW},c_limit/`
  );
};

const SiteImage = ({
  keyName,
  fallback,
  alt = "",
  loaderHeight = 300,
  onImgLoad,
  // NEW: caller can mark this as the LCP/hero image so the browser
  // prioritizes its download. Defaults to false (preserves old behavior).
  priority = false,
  // NEW: target render width in CSS pixels — used to ask Cloudinary for
  // an appropriately-sized image (dpr_auto handles retina on top).
  width = 1400,
  ...imgProps
}) => {
  const dispatch = useDispatch();
  const { imagesMap } = useSelector((s) => s.siteImages);
  const record = imagesMap?.[keyName];
  const rawSrc = record?.secureUrl || record?.url;

  // When the remote URL for `keyName` has failed to load (typically a
  // 404 from a Cloudinary asset that an admin deleted), we flip this
  // flag so we render the local PNG fallback instead of leaving a
  // broken-image icon on the page. Reset whenever the underlying remote
  // src changes (e.g. admin uploads a fresh image for the same key).
  const [remoteFailed, setRemoteFailed] = useState(false);
  useEffect(() => { setRemoteFailed(false); }, [rawSrc]);

  const src = remoteFailed
    ? (fallback || "")
    : (rawSrc ? optimizeCloudinary(rawSrc, width) : (fallback || ""));

  // Remember WHICH src finished loading instead of a true/false flag that
  // an effect resets. The old reset effect could run *after* a cached
  // image's load event (this happens when the hero switches between the
  // "side" and "stack" layouts and remounts), leaving the image hidden
  // behind the shimmer forever.
  const [loadedSrc, setLoadedSrc] = useState(null);
  const imgLoaded = Boolean(src) && loadedSrc === src;
  const setImgLoaded = () => setLoadedSrc(src);

  // If the browser already has the image (memory/disk cache) it can be
  // complete before React's onLoad is attached — check on mount too.
  const imgRef = useCallback(
    (node) => {
      if (node && node.complete && node.naturalWidth > 0) {
        setLoadedSrc(src);
        onImgLoad?.({
          naturalWidth: node.naturalWidth,
          naturalHeight: node.naturalHeight,
          ratio: node.naturalWidth / node.naturalHeight,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [src]
  );

  useEffect(() => {
    if (!record) dispatch(fetchSiteImages([keyName]));
  }, [dispatch, keyName, record]);

  // Priority hints for the hero image. Spread as a plain object so React
  // forwards `fetchpriority` to the DOM regardless of React version.
  //
  // NOTE: we deliberately do NOT set `loading="lazy"` for non-priority
  // images. Browsers' lazy-loading IntersectionObserver does not fire for
  // elements that are `display:none` — and the <img> below is rendered
  // with `display:none` until it loads (so the skeleton can show in its
  // place). The combination would silently never trigger the network
  // request, so the skeleton would shimmer forever and the local PNG
  // fallback would never appear (this is exactly what was happening to
  // the vision1/2/3 cards on first home-page visit). Letting the browser
  // use its default loading mode avoids the deadlock.
  const priorityAttrs = priority
    ? { fetchpriority: "high", loading: "eager" }
    : {};

  // Brand-aligned skeleton: cream + gold soft gradient + diagonal shimmer.
  // Replaces the generic spinner so the loading state feels intentional.
  const Skeleton = (
    <div
      className="hadacy-skeleton"
      style={{ height: loaderHeight }}
      aria-hidden="true"
    >
      <span className="hadacy-skeleton-shimmer" />
    </div>
  );

  if (!src) {
    return (
      <>
        <SkeletonStyles />
        {Skeleton}
      </>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <SkeletonStyles />
      {!imgLoaded && Skeleton}
      <img
        ref={imgRef}
        src={src}
        alt={record?.alt || alt}
        decoding="async"
        {...priorityAttrs}
        onLoad={(e) => {
          setImgLoaded();
          const { naturalWidth, naturalHeight } = e.currentTarget;
          onImgLoad?.({
            naturalWidth,
            naturalHeight,
            ratio: naturalWidth / naturalHeight,
          });
        }}
        onError={() => {
          // The remote (Cloudinary) URL we tried failed to load — almost
          // always because the asset was deleted server-side but a stale
          // entry still lives in localStorage / Redux. If we have a local
          // fallback prop, swap to it AND scrub the bad cache entry so
          // this self-heals across reloads. Also kick off a refetch so
          // Redux drops the stale record on its next merge.
          if (rawSrc && fallback && !remoteFailed) {
            removeFromSiteImagesCache(keyName);
            dispatch(fetchSiteImages([keyName]));
            setRemoteFailed(true); // triggers re-render with `src = fallback`
            return;
          }
          // No fallback to swap to (or fallback also failed) — reveal
          // whatever the <img> currently shows so we don't get stuck
          // shimmering forever.
          setImgLoaded();
        }}
        style={{ display: imgLoaded ? "block" : "none" }}
        {...imgProps}
      />
    </div>
  );
};

/* Inlined once-per-component but cheap; React de-dupes <style> tags. */
const SkeletonStyles = () => (
  <style>{`
    .hadacy-skeleton {
      position: relative;
      width: 100%;
      overflow: hidden;
      border-radius: inherit;
      background: linear-gradient(
        135deg,
        rgba(184,147,58,0.10) 0%,
        rgba(232,213,196,0.32) 55%,
        rgba(184,147,58,0.10) 100%
      );
    }
    .hadacy-skeleton-shimmer {
      position: absolute;
      inset: 0;
      background: linear-gradient(
        100deg,
        rgba(255,255,255,0)   30%,
        rgba(255,255,255,0.55) 50%,
        rgba(255,255,255,0)   70%
      );
      transform: translateX(-100%);
      animation: hadacyShimmer 1.6s ease-in-out infinite;
      will-change: transform;
    }
    @keyframes hadacyShimmer {
      0%   { transform: translateX(-100%); }
      100% { transform: translateX(100%);  }
    }
    @media (prefers-reduced-motion: reduce) {
      .hadacy-skeleton-shimmer { animation: none; opacity: 0.6; }
    }
  `}</style>
);

export default SiteImage;
